import React from 'react';
import {BrowserRouter as Router} from 'react-router';
import {createRoot} from 'react-dom/client';
import {
  EventType,
  AuthenticationResult,
  IPublicClientApplication
} from '@azure/msal-browser';
import {AccountInfo} from '@azure/msal-common';
import formbricks from '@formbricks/js';
import {datadogRum} from '@datadog/browser-rum';
import {anonymizeUsername, logMajorVersionMismatch} from '@/services/utils.ts';
import App from '@/app/App.tsx';
import reportWebVitals from '@/reportWebVitals.js';
import {initDataDog} from '@/app/datadog/datadog.init.ts';
import {basePath} from '@/Pages/Auth/auth.config.ts';

const {
  VITE_TRACKING_FORMBRICKS_HOST: apiHost,
  VITE_TRACKING_FORMBRICKS_ENV_ID: environmentId
} = import.meta.env;

// Detect stale/invalid ephemeral URL: staging build served for an /eph/ path means
// the ephemeral environment was cleaned up or never existed (S3 404 → CloudFront fallback)
if (basePath === '/' && globalThis.location.pathname.startsWith('/eph/')) {
  document.getElementById('root')!.innerHTML =
    '<div style="font-family: sans-serif; padding: 2rem; text-align: center; margin-top: 4rem">' +
    '<h1>Ephemeral environment not found</h1>' +
    '<p>This environment may have been cleaned up or does not exist.</p>' +
    '</div>';
  throw new Error('Ephemeral environment not found');
}

/**
 * Shared app initialization logic
 *
 * @param msalInstance - MSAL instance (real or mock depending on VITE_E2E_MODE)
 */
export async function initializeApp(msalInstance: IPublicClientApplication) {
  await msalInstance.initialize();

  // Configure active account
  const accounts = msalInstance.getAllAccounts();

  const setUserAccount = (account: AccountInfo) => {
    msalInstance.setActiveAccount(account);
    datadogRum.setUser({id: anonymizeUsername(account.username)});
  };

  if (accounts.length > 0) {
    setUserAccount(accounts[0]);
  }

  msalInstance.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      setUserAccount(account);
    }
  });

  // Log major version mismatch of outdated packages
  logMajorVersionMismatch();

  // Formbricks integration
  if (typeof window !== 'undefined') {
    formbricks.init({
      apiHost: apiHost,
      environmentId: environmentId,
      errorHandler: (error: any) => {
        console.error('Formbricks errorHandler:', error);
      }
    });
  }

  // Render app
  const container = document.getElementById('root') as HTMLElement;
  createRoot(container).render(
    <React.StrictMode>
      <Router basename={basePath}>
        <App msalInstance={msalInstance} />
      </Router>
    </React.StrictMode>
  );

  initDataDog();

  // Measure performance
  reportWebVitals();
}
